import { antwortBeobachten, netzBeobachten, netzStand } from '../api/client'

/**
 * Die letzte Absage, die sonst niemand zeigt (Codereview E2).
 *
 * **Der Anlass ist eine Liste von rund fünfzehn Stellen**, an denen etwas
 * angetippt wird und nichts passiert: einen Punkt löschen, freigeben oder
 * verbergen, die Sammelfreigabe, die Regler der Karte, die Routenoptionen,
 * eine Aufzeichnung entfernen, einen Entwurf verschieben, die Schalter der
 * Reise, das Abmelden. Jede prüft ihren Fehler nicht, und ohne Netz heißt das:
 * der Finger geht runter, und die Anwendung schweigt. Genau die stille
 * Absage, die in `CLAUDE.md` als die eine steht, die es nie wieder geben soll.
 *
 * **Ein Fangnetz und kein Ersatz.** Wo eine Stelle ihre Absage selbst
 * hinstellt — im Formular des Punktes, an der Eingabezeile —, ist sie besser
 * aufgehoben: sie steht dort, wo der Finger war. Diese Meldung ist für alles
 * andere, und eine Mutation sagt über `meta.eigeneMeldung` ab, dass sie sich
 * selbst kümmert.
 *
 * **Kein Zustand in React**, weil `MutationCache.onError` außerhalb jeder
 * Komponente läuft: ein Modul mit Zuhörern, wie `netzStand()` daneben.
 */
let letzte: string | null = null
/** Whether the last one failed for want of a net -- then the net takes it away. */
let amNetz = false
const zuhoerer = new Set<() => void>()

function sage(): void {
  for (const ruf of zuhoerer) ruf()
}

/**
 * Eine Absage merken -- sie ersetzt die vorige.
 *
 * **`amNetz`: sie lag am fehlenden Netz** (1.90.0). Aus dem Betrieb, mit
 * Bildschirmfoto: nach einem Start ohne Netz stand „Keine Verbindung zum
 * Server." rot da und blieb, als das Netz zurück war und die Bilder schon
 * wieder kamen -- weg ging sie nur mit dem Kreuz oder dem nächsten
 * gelungenen Speichern. Die Regel aus 1.80.0 sagt es anders: eine Meldung,
 * die am Netz liegt, geht mit dem Netz. Eine andere Absage (der Server hat
 * abgelehnt) bleibt, bis jemand sie gelesen hat.
 */
export function merkeAbsage(text: string, wegenNetz = false): void {
  letzte = text
  amNetz = wegenNetz
  sage()
}

/*
 * The net is back: a refusal that was only its absence has settled itself.
 *
 * **Two signs, because one fires only on a change.** `netzBeobachten` calls
 * when the app's mark flips; if the mark already stood on "net" when the
 * refusal came (a focus re-judged it, the failure took a way that does not
 * set it), nothing flips when the net returns -- measured in the full bench
 * run, 1180 points, while the run alone was green. An answer from the
 * server is the sign that does not guess, the same one the pictures use
 * since 1.50.1.
 */
function netzZurueck(): void {
  if (letzte !== null && amNetz && netzStand()) absageWeg()
}
netzBeobachten(netzZurueck)
antwortBeobachten(netzZurueck)
/*
 * **And a third: the browser's own flag** (1.94.0, #122). `netzStand()` is
 * made of two things, the app's mark and `navigator.onLine`, and only the
 * first had a listener here. When the mark already stood on "net" before the
 * browser said so -- an answer came in while it still said offline, or the
 * app re-judged the mark on its own `online` -- then nothing flipped when
 * the browser's flag came back, and the refusal stayed until some request
 * happened to answer. On the settings page that can be minutes. Measured in
 * `offlineabsage.mjs`; the run flickered one time in three for exactly
 * this, depending on whether a request from before was still in flight.
 */
if (typeof window !== 'undefined') window.addEventListener('online', netzZurueck)

/** Weg damit: der Mensch hat sie gelesen. */
export function absageWeg(): void {
  if (letzte === null) return
  letzte = null
  amNetz = false
  sage()
}

export function letzteAbsage(): string | null {
  return letzte
}

export function absagenBeobachten(ruf: () => void): () => void {
  zuhoerer.add(ruf)
  return () => {
    zuhoerer.delete(ruf)
  }
}
